import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import history from '../../history';
import { formatFiles } from '../../utils/attachmentSelector';
import useActiveHeirarchy from '../../utils/useActiveHeirarchy';
import { addComment, fetchCompanyUsers } from '../../api/v4';
import {
  FREQUENCY_OPTIONS,
  MONTHLY_FREQUENCY_OPTIONS,
  TASK_CATEGORY_DROPDOWN,
  YEARLY_FREQUENCY_OPTIONS
} from '../../constants/constants';
import {
  addUserTaskRequest,
  deleteUserTaskRequest,
  updateUserTaskRequst
} from '../../actions/tasks';
import {
  deleteAttachmentRequest,
  clearUploadedAttachments,
  addUploadedAttachmentsResponse
} from '../../actions/attachments';
import { getActiveTaskSelector } from '../../selectors/tasks';
import { getLoggedInUser } from '../../selectors/users';
import { getAddedAttachmentsSelector } from '../../selectors/attachments';
import customToast from '../../utils/customToast';
import {
  canSubmitImmediate,
  canSubmitRepeating,
  duplicateGroupAreaPairs,
  filterOutFullGroups
} from '../../utils/taskHelper';
import {
  getSingleCompanyGroupAreaDropdown,
  getSingleCompanyGroupDropdown,
  getSingleProjectDropdown
} from '../../utils/employeeDGPHelper';
import { getValidAssignees } from '../../utils/trainingHelper';
import AttachmentUploadCard from '../../components/AttachmentUploadCard';
import Card from '../../components/Card';
import CommentCard from './comments';
import {
  Button,
  Checkbox,
  Dropdown,
  EmployeeDropdown,
  Textbox,
  TwoColumn
} from '../../components/inputs';
import DayOfWeekButtons from '../../components/inputs/DayOfWeekButtons';
import DeleteItemModal from '../../components/Modal/deleteItemModal';
import DueBy from '../../components/inputs/DueBy';
import Header from '../../components/Header';
import HeaderAndFooter from '../../components/HeaderAndFooter';
import HierarchySelector from '../../components/HierarchySelector';
import { IReportDatePicker } from '../../components/inputs/DateTimePicker';
import LeftNav from '../../components/LeftNav';
import MarkItemAsCompleteModal from '../../components/Modal/markItemAsCompleteModal';
import MissingRequiredModal from '../../components/Modal/missingRequiredModal';
import Modal from '../../components/Modal';
import RadioButtons from '../../components/inputs/RadioButtons';
import ReassignUserModal from '../../components/Modal/reassignUserModal';
import {
  SaveCancelFooter,
  SubmitSaveForLaterCancelFooter
} from '../../components/Footer';
import SaveChangesModal from '../../components/Modal/saveChangesModal';
import UserBadge from '../../components/UserBadge';

import styles from './task.module.scss';

export default function TaskContainer(props) {
  const dispatch = useDispatch();

  const createTask = task => dispatch(addUserTaskRequest(task));
  const updateTask = task => dispatch(updateUserTaskRequst(task));

  const activeTask = useSelector(getActiveTaskSelector);
  const loggedInUser = useSelector(getLoggedInUser);
  const addedAttachments = useSelector(getAddedAttachmentsSelector);
  const locked = props.locked;
  const { activeCompany, activeGroup, activeProject } = useActiveHeirarchy();
  const [openModalType, setOpenModalType] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [task, setTask] = useState({});
  const [comments, setComments] = useState([]);
  const [attachmentsToUpload, setAttachmentsToUpload] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [attachmentsAdded, setAttachmentsAdded] = useState([]);
  const [newOwnerId, setNewOwnerId] = useState();
  const [missingRequired, setMissingRequired] = useState(false);
  const [missingModalOpen, setMissingModalOpen] = useState(false);
  const [users, setUsers] = useState([]);

  const clearAttachments = () => {
    activeTask?.returnPath && activeTask?.incidentAttachments?.length
      ? dispatch(addUploadedAttachmentsResponse(activeTask.incidentAttachments))
      : dispatch(clearUploadedAttachments());
  };

  const {
    assignedTo,
    createdBy,
    currentCategory,
    description,
    dueByNumber,
    dueBy,
    dueByTime,
    dueDate,
    frequency,
    groupId,
    isCompleted,
    isPaused,
    label,
    ownerType,
    projectId,
    repeatingOn,
    schedule,
    taskOwner,
    isArchived,
    groupAssigneePairs,
    assignOn,
    equipmentGroupDropdown,
    equipmentProjectDropdown,
    otherCategory
  } = task;

  const group = activeCompany.groups?.find(g => g._id === groupId);
  const area = group?.projects?.find(p => p._id === projectId);

  const hiddenOrArchived = isArchived || group?.isHidden || area?.isHidden;

  const disabled = (!editing && !creating) || locked || hiddenOrArchived;
  const creator = createdBy ?? task?.createdByUserId;

  const isCreator = !creator || creator === loggedInUser._id;
  const adminOrCreator =
    isCreator ||
    [500, 900].indexOf(loggedInUser.accessLevel) > -1 ||
    loggedInUser._id === taskOwner;

  const isRepeating = schedule === 'repeating';

  const validDueDate =
    creating && !dueDate
      ? false
      : dueDate && creating
        ? moment(dueDate).isAfter(moment(assignOn))
        : true;

  const isFromEquipment = ownerType === 'equipment';
  const isForIncident = ownerType === 'incident';
  const isReportComponentTask =
    ownerType === 'incident' && !!activeTask?.reportComponentName;

  const selectedGroupsWithAreas = activeCompany?.groups
    ?.filter(
      g =>
        task?.groupAssigneePairs?.flatMap(g => g.groupId)?.includes(g._id) &&
        g?.projects?.length
    )
    ?.map(g => g._id);

  const requiredAreasFulfilled = creating
    ? selectedGroupsWithAreas?.length
      ? task?.groupAssigneePairs
          ?.filter(gap => selectedGroupsWithAreas?.includes(gap?.groupId))
          ?.every(gap => gap.projectId)
      : true
    : group?.projects?.length
      ? !!projectId
      : true;

  const canSubmit = isRepeating
    ? canSubmitRepeating(task, creating, requiredAreasFulfilled)
    : canSubmitImmediate(task, creating, requiredAreasFulfilled);

  useEffect(() => {
    fetchCompanyUsers().then(setUsers);
  }, []);

  useEffect(() => {
    const { incidentAttachments, ...activeTaskForForm } = activeTask ?? {};
    setTask({ ...activeTaskForForm });

    if (!activeTask?._id) {
      setCreating(true);
      setTask({
        groupId: activeGroup?._id,
        projectId: activeProject?._id,
        ...activeTaskForForm,
        assignOn: isReportComponentTask ? moment().startOf('days') : null
      });
    }

    if (
      !activeTask.isCompleted &&
      activeTask.assignedTo?.some(assignee => assignee === loggedInUser._id) &&
      activeTask?.schedule !== 'repeating'
    )
      setEditing(true);
    setComments(activeTask?.comments ?? []);
  }, [
    activeTask,
    loggedInUser,
    isReportComponentTask,
    activeGroup?._id,
    activeProject?._id
  ]);

  useEffect(() => {
    setAttachmentsAdded(addedAttachments);
  }, [addedAttachments]);

  const handleAnswer = values => {
    setHasUnsavedChanges(true);
    setTask({ ...task, ...values });
  };

  const handleTaskSubmit = async (isCompleted = false) => {
    if (creating) {
      if (filesToUpload?.length) {
        let data = formatFiles(filesToUpload);
        await createTask({
          ...task,
          attachmentData: {
            data: data,
            ownerType: 'task',
            photoType: 'defective'
          },
          incidentAttachments: activeTask?.incidentAttachments ?? []
        });
      } else {
        await createTask({
          ...task,
          isCompleted,
          incidentAttachments: activeTask?.incidentAttachments ?? []
        });
      }
      setCreating(false);
      setHasUnsavedChanges(false);
      clearAttachments();
      if (isRepeating) setOpenModalType('createRepeating');
    } else {
      await updateTask({ ...task, isCompleted });
      clearAttachments();
      history.goBack();
    }
  };

  const handleDeleteTask = () => {
    const allAttachments = [
      ...(addedAttachments || []),
      ...(activeTask.photosToUpload || [])
    ];
    dispatch(deleteUserTaskRequest({ ...task, attachments: allAttachments }));
  };

  const addNewComment = async (comment, parentId) => {
    let fullComment = {
      value: comment,
      ownerId: task._id,
      ownerType: 'task',
      parentId
    };
    addComment(fullComment).then(newComment => {
      setComments([...comments, newComment]);
    });
  };

  const handleDiscardTask = () => {
    clearAttachments();
    activeTask.returnPath
      ? history.push(activeTask.returnPath)
      : history.goBack();
  };

  const deleteAttachment = async attachment => {
    dispatch(deleteAttachmentRequest(attachment));
    customToast(`Deleted Updated!`, 'success');
    let updatedAttachments = activeTask?.attachments?.filter(
      a => a !== attachment
    );
    setHasUnsavedChanges(creating || editing);
    if (!creating)
      await updateTask({
        ...task,
        comments,
        attachments: updatedAttachments
      });
  };

  const addAttachments = async (attachments, photoType = '') => {
    let data = formatFiles(attachments);
    let attachmentData = {
      data,
      ownerId: activeTask._id ?? 'pending',
      ownerType: 'task',
      photoType: photoType
    };
    // need to update the task so the attachment array has the new photo ids
    await updateTask({
      ...task,
      comments,
      _id: activeTask._id,
      attachmentData
    });
  };

  const renderRepeatingDueDateOptions = () => {
    return (
      <>
        <Dropdown
          options={FREQUENCY_OPTIONS}
          fieldLabel="Frequency"
          currentValue={frequency}
          onChange={v => handleAnswer({ frequency: v, repeatingOn: undefined })}
          isRequired={true}
          disabled={disabled || !adminOrCreator}
          touched={missingRequired && !frequency}
        />
        {frequency === 'weekly' ? (
          <DayOfWeekButtons
            fieldLabel="Assign Every:"
            inputClassName="dayOfWeekInput"
            disabled={disabled || !adminOrCreator}
            onChange={v => handleAnswer({ repeatingOn: v })}
            currentValue={repeatingOn}
            isRequired
            touched={missingRequired && !repeatingOn?.length}
          />
        ) : frequency !== 'quarterly' ? (
          <Dropdown
            options={
              frequency === 'monthly'
                ? MONTHLY_FREQUENCY_OPTIONS
                : YEARLY_FREQUENCY_OPTIONS
            }
            fieldLabel="Assign Every:"
            currentValue={repeatingOn}
            onChange={v => handleAnswer({ repeatingOn: [v] })}
            isRequired
            disabled={disabled || !adminOrCreator}
            touched={missingRequired && !repeatingOn?.length}
          />
        ) : (
          <></>
        )}
        <DueBy
          fieldLabel="Due By"
          isRequired
          disabled={disabled || !adminOrCreator}
          name="dueBy"
          dueByNumber={dueByNumber}
          dueByType={dueBy}
          dueTime={dueByTime}
          onDueByNumberChange={v => handleAnswer({ dueByNumber: v })}
          onDueByTypeChange={v => handleAnswer({ dueBy: v })}
          onDueTimeChange={v => handleAnswer({ dueByTime: v })}
          touched={missingRequired}
          isTask
        />
      </>
    );
  };

  const renderCreatingAssignedTo = () => {
    let groupDropdown =
      equipmentGroupDropdown ??
      getSingleCompanyGroupAreaDropdown(activeCompany);

    return (
      <>
        {isReportComponentTask || isForIncident ? null : (
          <div className={styles.adminContainer}>
            <span className={styles.adminInfo}>
              One task will be created for each Group/Establishment selected
            </span>
          </div>
        )}
        {groupAssigneePairs?.map((group, index) => {
          const filteredGroupDropdown = filterOutFullGroups(
            groupDropdown,
            groupAssigneePairs,
            group.groupId
          );
          let projectDropdown =
            equipmentProjectDropdown?.filter(
              d => d.groupId === group?.groupId
            ) ?? getSingleProjectDropdown(activeCompany, group.groupId);
          // this makes sure the same area cannot be selected multiple times
          const areasSelected = groupAssigneePairs
            ?.map(g => g.projectId)
            ?.filter(p => p !== group?.projectId);

          projectDropdown = projectDropdown?.filter(
            d => !areasSelected?.includes(d?.value)
          );

          const alreadyUsed = duplicateGroupAreaPairs(groupAssigneePairs);
          return (
            <>
              <TwoColumn className={styles.specialGrid}>
                <TwoColumn>
                  <HierarchySelector
                    groupId={group?.groupId}
                    filteredGroupDropdown={filteredGroupDropdown}
                    filteredProjectDropdown={
                      projectDropdown?.length
                        ? [
                            { value: '', label: ' ' },
                            ...(projectDropdown ?? [])
                          ]
                        : projectDropdown
                    }
                    groupDisabled={isForIncident || isReportComponentTask}
                    noTwoColumn
                    alwaysShowGroupDropdown
                    alwaysShowProjectDropdown
                    onGroupChange={values => {
                      let newGroup = {
                        ...groupAssigneePairs[index],
                        groupId: values.value,
                        projectId:
                          groupAssigneePairs[index] === values
                            ? groupAssigneePairs[index]?.projectId
                            : ''
                      };
                      newGroup.assignees = getValidAssignees(newGroup, users);
                      groupAssigneePairs[index] = newGroup;
                      let used = duplicateGroupAreaPairs(groupAssigneePairs);
                      handleAnswer({
                        groupAssigneePairs: groupAssigneePairs,
                        alreadyUsed: used
                      });
                    }}
                    onProjectChange={values => {
                      let newGroup = {
                        ...groupAssigneePairs[index],
                        projectId: values
                      };
                      groupAssigneePairs[index] = newGroup;
                      let used = duplicateGroupAreaPairs(groupAssigneePairs);
                      handleAnswer({
                        groupAssigneePairs: groupAssigneePairs,
                        alreadyUsed: used
                      });
                    }}
                    groupTouched={missingRequired && !group?.groupId}
                    projectId={group?.projectId}
                    projectTouched={
                      alreadyUsed || (missingRequired && !group?.projectId)
                    }
                    projectDisabled={isForIncident || isReportComponentTask}
                    projectError={
                      alreadyUsed
                        ? 'This group/area pair is already used. Please choose a different combination'
                        : ''
                    }
                  />

                  <EmployeeDropdown
                    permissions={[100, 400, 500, 900]}
                    fieldLabel="Assignee(s)"
                    currentValue={group.assignees}
                    onChange={assignees => {
                      let newAdmin = {
                        ...groupAssigneePairs[index],
                        assignees
                      };
                      groupAssigneePairs[index] = { ...newAdmin };
                      handleAnswer({
                        groupAssigneePairs: groupAssigneePairs
                      });
                    }}
                    searchable
                    name="dropdownEmployees"
                    isRequired
                    multi
                    disabled={disabled}
                    touched={missingRequired && !group?.assignees?.length}
                    group={group?.groupId}
                    selectButtons
                    limit={
                      (isReportComponentTask || isForIncident) &&
                      !!activeTask?.permittedAssignees?.length
                        ? activeTask?.permittedAssignees
                        : null
                    }
                  />
                  {isReportComponentTask ? (
                    <></>
                  ) : (
                    <Checkbox
                      fieldLabel="Assign Individually"
                      name="isIndividual"
                      onChange={v => {
                        let newAdmin = {
                          ...groupAssigneePairs[index],
                          isIndividual: v
                        };
                        groupAssigneePairs[index] = { ...newAdmin };
                        handleAnswer({
                          groupAssigneePairs: groupAssigneePairs
                        });
                      }}
                      currentValue={group?.isIndividual}
                      disabled={
                        disabled ||
                        (isRepeating ? !creating && !editing : !creating)
                      }
                    />
                  )}
                </TwoColumn>
                {groupAssigneePairs?.length > 1 ? (
                  <img
                    type="button"
                    src={require('../../assets/images/remove.png')}
                    alt="delete group"
                    onClick={() => {
                      groupAssigneePairs.splice(index, 1);
                      handleAnswer({
                        groupAssigneePairs: groupAssigneePairs
                      });
                    }}
                    className={styles.removeImage}
                    data-cy="removeButton"
                  />
                ) : null}
              </TwoColumn>
              {index < groupAssigneePairs?.length - 1 ? (
                <div className={styles.divider} />
              ) : (
                <></>
              )}
            </>
          );
        })}
        {isReportComponentTask || isForIncident ? null : (
          <Button
            text="Add Group"
            color={
              missingRequired && !groupAssigneePairs?.length ? 'red' : 'blue'
            }
            onClick={() =>
              handleAnswer({
                groupAssigneePairs: (groupAssigneePairs ?? []).concat({
                  groupId: activeGroup?._id ?? null,
                  projectId: activeProject?._id ?? null
                })
              })
            }
            inputClassName={styles.button}
            disabled={disabled}
          />
        )}
      </>
    );
  };

  const renderViewingAssignedTo = () => {
    const groupDropdown = getSingleCompanyGroupDropdown(activeCompany);
    return (
      <>
        <HierarchySelector
          groupId={groupId}
          projectId={projectId}
          filteredGroupDropdown={groupDropdown}
          groupDisabled={
            disabled ||
            !adminOrCreator ||
            isReportComponentTask ||
            isForIncident
          }
          projectDisabled={
            disabled ||
            !adminOrCreator ||
            isReportComponentTask ||
            isForIncident
          }
          projectRequired={!!group?.projects?.length}
          projectTouched={missingRequired && !projectId}
          noTwoColumn
          alwaysShowGroupDropdown
          onGroupChange={v => handleAnswer({ groupId: v, projectId: null })}
          onProjectChange={v => handleAnswer({ projectId: v })}
          groupBareValues
        />
        <EmployeeDropdown
          permissions={[100, 400, 500, 900]}
          fieldLabel="Assignee(s)"
          currentValue={assignedTo}
          searchable
          name="dropdownEmployees"
          isRequired
          multi
          disabled={disabled || !adminOrCreator}
          onChange={v => handleAnswer({ assignedTo: v })}
        />
      </>
    );
  };

  const header = (
    <Header
      title="Tasks"
      section={`${creating ? 'Create' : editing ? 'Edit' : 'View'} Task`}
      needsSaved={hasUnsavedChanges}
      clickBack={() =>
        hasUnsavedChanges
          ? setOpenModalType('unsavedChanges')
          : handleDiscardTask()
      }
      pageActionOptions={
        adminOrCreator && !creating
          ? [
              {
                label: 'Edit Task',
                visible: !editing && !hiddenOrArchived,
                color: 'blueOutline',
                onClick: () => setEditing(true)
              },
              {
                label: 'Delete Task',
                visible: editing || activeTask.isArchived,
                color: 'redOutline',
                onClick: () =>
                  setOpenModalType(isRepeating ? 'endRepeating' : 'deleteCa')
              },
              {
                label: 'Reopen Task',
                visible: !hiddenOrArchived && isCompleted && editing,
                color: 'greenOutline',
                onClick: () => {
                  updateTask({
                    ...task,
                    isCompleted: !isCompleted
                  });
                }
              },
              {
                label: 'Transfer Ownership',
                visible:
                  !hiddenOrArchived &&
                  !isCompleted &&
                  activeTask.schedule !== 'repeating' &&
                  adminOrCreator &&
                  editing,
                color: 'blueOutline',
                onClick: () => setOpenModalType('transferOwner')
              },
              {
                label: `${activeTask?.isArchived ? 'Un-' : ''}Archive Task`,
                color: 'blueOutline',
                onClick: () => {
                  updateTask({ ...task, isArchived: !task.isArchived });
                  if (!task.isArchived) history.push('/app/tasks/task');
                },
                visible:
                  loggedInUser.accessLevel > 400 &&
                  !activeTask?.fromRepeating &&
                  activeTask.canEdit
              }
            ]
          : null
      }
    />
  );

  const footer =
    creating || (activeTask?.fromRepeating && editing) ? (
      <SaveCancelFooter
        saveButtonClick={() =>
          canSubmit ? handleTaskSubmit() : setMissingModalOpen(true)
        }
        saveButtonText={editing ? 'Update' : 'Create'}
        cancelButtonClick={() =>
          hasUnsavedChanges
            ? setOpenModalType('unsavedChanges')
            : handleDiscardTask()
        }
        editing={editing}
        onMouseEnter={() => setMissingRequired(!canSubmit)}
      />
    ) : editing ? (
      <SubmitSaveForLaterCancelFooter
        saveButtonClick={() => handleTaskSubmit()}
        saveButtonDisabled={!hasUnsavedChanges || !canSubmit}
        saveButtonText="Save"
        submitButtonText="Complete"
        cancelButtonClick={() =>
          hasUnsavedChanges
            ? setOpenModalType('unsavedChanges')
            : handleDiscardTask()
        }
        deleteButtonClick={() => setOpenModalType('delete')}
        submitButtonClick={() =>
          canSubmit ? setOpenModalType('complete') : setMissingModalOpen(true)
        }
        onMouseEnter={() => setMissingRequired(!canSubmit)}
        submitButtonDisabled={hiddenOrArchived}
      />
    ) : null;

  let leftNav = [{ label: 'Task Details', id: '0' }];
  if (ownerType !== 'incident') leftNav.push({ label: 'Attachments', id: '1' });
  if (!creating) leftNav.push({ label: 'Comments', id: '2' });

  const assignedOnDate = moment(task?.assignOn ?? task?.createdAt).format(
    'MM/DD/YYYY'
  );

  return (
    <>
      <HeaderAndFooter
        Header={header}
        Footer={footer}
        showFooter
        lockInfo={props.lockInfo}
        Left={<LeftNav items={leftNav} />}
      >
        <Card showHeader title="Task Details" name={0}>
          {creating ? null : (
            <div className={styles.stats}>
              <UserBadge
                byline="Created By"
                userId={creator}
                showImage
                showName
              />
              <div className={styles.text}>
                <div>Assigned On</div>
                <div style={{ fontWeight: 'bold' }}>{assignedOnDate}</div>
              </div>

              {task?.incidentNumber ? (
                <div className={styles.text}>
                  <div>Incident ID</div>
                  <div style={{ fontWeight: 'bold' }}>
                    {task?.incidentNumber}
                  </div>
                </div>
              ) : null}
            </div>
          )}
          <Textbox
            currentValue={label}
            fieldLabel="Title"
            onChange={label => handleAnswer({ label })}
            placeholder="Type a clear and concise title."
            isRequired
            disabled={disabled || !adminOrCreator}
            touched={missingRequired && !label}
          />
          <Textbox
            currentValue={description}
            fieldLabel="Description"
            onChange={description => handleAnswer({ description })}
            type="textarea"
            placeholder="Describe what needs to be done to complete this Task. Be sure to include any important details."
            isRequired
            disabled={disabled || !adminOrCreator}
            touched={missingRequired && !description}
          />
          <Dropdown
            options={TASK_CATEGORY_DROPDOWN}
            fieldLabel="Category/Reason"
            currentValue={currentCategory}
            onChange={v =>
              handleAnswer({
                currentCategory: v,
                otherCategory: v === 'Other' ? otherCategory : ''
              })
            }
            disabled={disabled || isReportComponentTask || !adminOrCreator}
            isRequired
            multi
            placeholder="Choose one or more options"
            touched={missingRequired && !currentCategory}
            alphabetize
            lastOptions={['Other']}
          />
          {currentCategory?.includes(16) ? (
            <Textbox
              fieldLabel="Other"
              onChange={v => handleAnswer({ otherCategory: v })}
              currentValue={otherCategory}
              disabled={disabled || !adminOrCreator}
            />
          ) : null}
          {isForIncident || !creating ? null : (
            <RadioButtons
              fieldLabel="Schedule"
              options={[
                { value: 'immediate', label: 'Immediately' },
                { value: 'repeating', label: 'Repeating' }
              ]}
              isRequired
              onChange={v => {
                const changedAnswer = v !== schedule;
                handleAnswer({
                  schedule: v,
                  assignOn: changedAnswer ? null : assignOn,
                  frequency: changedAnswer ? null : frequency,
                  dueDate: changedAnswer ? null : dueDate,
                  repeatingOn: changedAnswer ? null : repeatingOn,
                  dueBy: changedAnswer ? null : dueBy,
                  dueByTime: changedAnswer ? null : dueByTime,
                  dueByNumber: changedAnswer ? null : dueByNumber,
                  groupAssigneePairs: groupAssigneePairs?.length
                    ? groupAssigneePairs
                    : [{}]
                });
              }}
              currentValue={schedule}
              disabled={disabled || editing || isFromEquipment}
              touched={missingRequired && !schedule}
            />
          )}
          {creating ? renderCreatingAssignedTo() : renderViewingAssignedTo()}
          {isRepeating ? (
            renderRepeatingDueDateOptions()
          ) : (
            <>
              <IReportDatePicker
                fieldLabel="Assign On"
                isRequired
                onChange={v =>
                  handleAnswer({
                    assignOn: v,
                    dueDate: moment(v).isAfter(dueDate) ? null : dueDate
                  })
                }
                currentValue={assignOn ?? task?.createdAt}
                touched={missingRequired && !assignOn}
                pickDate
                timeNotDetermined
                name="assignOn"
                disabled={!creating || isReportComponentTask}
                minDate={new Date()}
              />
              <IReportDatePicker
                fieldLabel="Due By"
                isRequired
                onChange={value => handleAnswer({ dueDate: value })}
                pickTime
                currentValue={dueDate}
                disabled={
                  disabled || !adminOrCreator || (creating && !assignOn)
                }
                name="taskDueBy"
                touched={(missingRequired && !dueDate) || !validDueDate}
                minDate={new Date(moment(assignOn).add(1, 'days'))}
                errorMessage={
                  validDueDate ? 'Must be a future date and time' : null
                }
              />
            </>
          )}
          {isRepeating && !creating ? (
            <Checkbox
              fieldLabel="Pause Task"
              name="pauseTask"
              onChange={value => handleAnswer({ isPaused: value })}
              currentValue={isPaused}
              disabled={disabled || !adminOrCreator}
            />
          ) : (
            <></>
          )}
        </Card>
        {isReportComponentTask || isRepeating ? null : (
          <AttachmentUploadCard
            addAttachments={addAttachments}
            deleteAttachment={deleteAttachment}
            isCreating={creating}
            isEditing={
              adminOrCreator
                ? editing
                : task?.assignedTo?.includes(loggedInUser._id)
            }
            addedAttachments={attachmentsAdded}
            attachmentsToUpload={attachmentsToUpload}
            name={1}
            handleAnswer={(currentAttachments, currentFiles) => {
              setAttachmentsToUpload(currentAttachments);
              setFilesToUpload(currentFiles);
              setHasUnsavedChanges(true);
            }}
            taskOwnedBy={ownerType}
            ownerType="task"
            formattedAttachmentsToUpload={filesToUpload}
            keepAttachmentsAnonymous={ownerType === 'safetySuggestion'}
            disabled={hiddenOrArchived}
          />
        )}
        {creating || isRepeating ? null : (
          <CommentCard
            name={2}
            addComment={addNewComment}
            comments={comments}
            disabled={hiddenOrArchived}
          />
        )}
      </HeaderAndFooter>
      <SaveChangesModal
        isOpen={openModalType === 'unsavedChanges'}
        onRequestClose={() => setOpenModalType()}
        submitActions={handleDiscardTask}
        savingWhat="Task"
      />
      <DeleteItemModal
        isOpen={openModalType === 'deleteCa'}
        onRequestClose={() => setOpenModalType()}
        deletingWhat="Task"
        hideCustomTemplate
        submitActions={handleDeleteTask}
      />
      <MarkItemAsCompleteModal
        isOpen={openModalType === 'complete'}
        completingWhat="Task"
        onRequestClose={() => setOpenModalType()}
        submitActions={() => handleTaskSubmit(true)}
        cancelActions={() => setOpenModalType()}
        message={
          <div>
            Are you sure you would like to mark this task complete? This will
            remove the task from the assignee(s) to-do list.
          </div>
        }
        submitButtonColor="blue"
        submitButtonText="Complete Task"
        cancelButtonText="Cancel"
        cancelButtonColor="white"
        headerColor="blueHeader"
      />
      <ReassignUserModal
        title="Transfer Ownership"
        previousUser={taskOwner}
        reassignUser={newOwnerId}
        disableSubmit={!newOwnerId}
        message={
          <div>
            From here, you can assign a different user to be the owner for this
            task. Task Ownership can only be reassigned to an Admin or
            Collaborator User with access to the Group/Establishment this task
            is in.
          </div>
        }
        onRequestClose={() => {
          setOpenModalType();
          setNewOwnerId();
        }}
        isOpen={openModalType === 'transferOwner'}
        employeeFieldLabel="New Task Owner"
        onChange={setNewOwnerId}
        submitActions={() => {
          setOpenModalType();
          setNewOwnerId();
          updateTask({
            ...task,
            taskOwner: newOwnerId
          });
        }}
        permissions={[500, 900]}
        groups={groupId}
        submitButtonText="Reassign"
      />
      <Modal
        title="Repeating Task"
        titleClassName="blueHeader"
        isOpen={openModalType === 'createRepeating'}
        onRequestClose={() => history.goBack()}
        hideButtons={true}
        testID="repeatingTask"
      >
        <div className="reportSectionContainer-emailAssigneeText">
          A Repeating Task has been created.{' '}
          <span
            style={{
              fontWeight: 'bold',
              fontStyle: 'italic'
            }}
          >
            It can be found in the Repeating section of the Tasks page. Editing
            or ending the Repeating Task will only affect future Tasks, and will
            not alter any Tasks that are already Active
          </span>
        </div>
      </Modal>
      <Modal
        title="End Repeating Task"
        titleClassName="redHeader"
        isOpen={openModalType === 'endRepeating'}
        submitButtonColor="red"
        submitButtonText="Delete Task"
        onRequestClose={() => setOpenModalType()}
        submitActions={handleDeleteTask}
      >
        <div className="reportSectionContainer-emailAssigneeText">
          <span style={{ color: '#c74846', fontWeight: 'bold' }}>
            Ending a Repeating Task will remove it from the Repeating section of
            the Task page.{' '}
          </span>
          Tasks that are already Active will not be affected, but no more will
          be created by this Repeating Task.
        </div>
      </Modal>
      <MissingRequiredModal
        isOpen={missingModalOpen}
        onRequestClose={() => setMissingModalOpen(false)}
        subject="Task"
        isEditing={editing}
        isCompleting
      />
    </>
  );
}
